import React, { useEffect, useRef, useState } from "react";
import {
  ArchiveIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  EnvelopeIcon,
  DocumentOpenIcon,
  MoreIcon,
  UndoIcon,
} from "evergreen-ui";
import { exportFilename, formatCurrency, statusClass } from "../invoiceUtils";
import { downloadInvoice } from "../invoiceExport";
import { IconButton } from "./SharedUI";
import { getVisualViewportBounds, placeFloatingPanel } from "./floatingPosition";
import InvoiceStatusControl from "./InvoiceStatusControl";
import { usePrivacy } from "../privacy/PrivacyContext";
import { useUiLanguage } from "../uiLanguage";

const exportLabel = (extension) => ({ pdf: "PDF", xlsx: "Excel", csv: "CSV" }[extension] || extension.toUpperCase());

const InvoiceActionMenu = ({ invoice, issuer, clients, position, menuRef, close, notify, openInvoice, reuseInvoice, archiveInvoice, restoreInvoice, downloadAccountDocument }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const operation = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => operation.current?.abort(), []);
  const runExport = async (extension) => {
    if (operation.current) return;
    const abort = new AbortController(); operation.current = abort; setBusy(true);
    try {
      if (downloadAccountDocument) {
        const result = await downloadAccountDocument(invoice, abort.signal, extension);
        const { saveAccountFile } = await import('../accountFileDownload');
        if (!abort.signal.aborted) notify(`Downloaded ${saveAccountFile(result)}`);
      } else {
        const filename = await downloadInvoice({ invoice, issuer, clients, format: extension });
        if (!abort.signal.aborted) notify(`Downloaded ${filename}`);
      }
    } catch (error) {
      if (!abort.signal.aborted) notify(error.message || `Could not download ${exportLabel(extension)}. Please try again.`);
    } finally {
      if (!abort.signal.aborted) { setBusy(false); close(); }
      operation.current = null;
    }
  };

  return <>
    <button className="is2-menu-dismiss" aria-label="Close invoice actions" onClick={close} />
    <div ref={menuRef} className="is2-inline-menu is2-invoice-action-menu" role="menu" aria-label={`Actions for ${privacy.invoice(invoice)}`} style={position}>
      <header className="is2-inline-menu__context">
        <span><small>{t("Invoice actions")}</small><strong>{privacy.invoice(invoice)} - {privacy.work(invoice.work)}</strong><em>{privacy.client(invoice.client)}</em></span>
        <span className={invoice.archived ? "is2-status is2-status--archived" : statusClass(invoice.status)}>{t(invoice.archived ? "Archived" : invoice.status)}</span>
      </header>
      <button role="menuitem" onClick={() => { close(); openInvoice(invoice); }}><EditIcon size={15} /><span><strong>{t(privacy.enabled ? "Open private preview" : invoice.status === "Draft" ? "Edit draft" : "Open invoice")}</strong><small>{t("View details and activity")}</small></span></button>
      {!invoice.archived ? <button role="menuitem" onClick={() => { close(); reuseInvoice(invoice); }}><DuplicateIcon size={15} /><span><strong>{t("Reuse as draft")}</strong><small>Keep the client and work, refresh dates and number</small></span></button> : null}
      {!privacy.enabled && !invoice.archived ? <>
        <button role="menuitem" disabled={busy} onClick={() => runExport("pdf")}><DownloadIcon size={15} /><span><strong>{t("Download PDF")}</strong><small>{exportFilename(invoice, "pdf")}</small></span></button>
        <button role="menuitem" disabled={busy} onClick={() => runExport("xlsx")}><DocumentOpenIcon size={15} /><span><strong>{t("Download Excel")}</strong><small>{exportFilename(invoice, "xlsx")}</small></span></button>
        <button role="menuitem" disabled={busy} onClick={() => runExport("csv")}><DocumentOpenIcon size={15} /><span><strong>{t("Download CSV")}</strong><small>{exportFilename(invoice, "csv")}</small></span></button>
        <button role="menuitem" disabled={busy || invoice.status?.toLowerCase() === "void"} onClick={() => { close({ restoreFocus: false }); openInvoice(invoice, { intent: "email" }); }}><EnvelopeIcon size={15} /><span><strong>{t("Prepare email")}</strong><small>Review before sending</small></span></button>
        <button className="is-danger" role="menuitem" onClick={() => { archiveInvoice(invoice.id); close(); }}><ArchiveIcon size={15} /><span><strong>{t("Archive invoice")}</strong><small>Recoverable from Archived</small></span></button>
      </> : !privacy.enabled ? <button role="menuitem" onClick={() => { restoreInvoice(invoice.id); close(); }}><UndoIcon size={15} /><span><strong>{t("Restore invoice")}</strong><small>Return it to the active list</small></span></button> : null}
    </div>
  </>;
};

const InvoiceTable = ({ rows, openInvoice, reuseInvoice, notify, archiveInvoice, restoreInvoice, updateInvoiceStatus, downloadAccountDocument, businesses = [], clients = [], showIssuer = false, compact = false }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const [actionMenu, setActionMenu] = useState(null);
  const actionMenuRef = useRef(null);
  useEffect(() => {
    if (!actionMenu) return undefined;
    const closeMenu = () => setActionMenu(null);
    const closeOnScroll = (event) => {
      if (event.target instanceof Node && actionMenuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    const mobileSheet = window.matchMedia("(max-width: 860px)").matches;
    const lockedScrollY = window.scrollY || 0;
    const previousBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    if (mobileSheet) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.width = "100%";
    }
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      const trigger = actionMenu.trigger;
      setActionMenu(null);
      window.setTimeout(() => trigger?.focus(), 0);
    };
    window.addEventListener("resize", closeMenu);
    if (!mobileSheet) window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", closeMenu);
      if (!mobileSheet) window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("keydown", closeOnEscape);
      if (mobileSheet) {
        document.body.style.overflow = previousBodyStyle.overflow;
        document.body.style.position = previousBodyStyle.position;
        document.body.style.top = previousBodyStyle.top;
        document.body.style.width = previousBodyStyle.width;
        window.scrollTo(0, lockedScrollY);
      }
    };
  }, [actionMenu]);
  return (
    <>
      <div className={`is2-table-wrap${compact ? " is2-table-wrap--compact" : ""}`}><table className="is2-table">
        <thead><tr><th>{t("Invoice")}</th><th>{t("Client")}</th><th>{t("Issued")}</th><th>{t("Due")}</th><th>{t("Status")}</th><th className="is-number">{t("Amount")}</th><th><span className="is2-visually-hidden">{t("Actions")}</span></th></tr></thead>
        <tbody>
          {rows.map((invoice) => {
            const issuer = businesses.find((business) => business.id === invoice.issuerId);
            return (
            <tr key={invoice.id} className={invoice.archived ? "is-archived" : ""} onClick={() => openInvoice(invoice)}>
              <td><strong>{privacy.invoice(invoice)}</strong><small>{showIssuer && issuer ? `${issuer.name} - ${privacy.work(invoice.work)}` : privacy.work(invoice.work)}</small></td>
              <td>{privacy.client(invoice.client)}</td><td>{privacy.date(invoice.issued)}</td><td>{privacy.date(invoice.due)}</td>
              <td>{invoice.archived || !updateInvoiceStatus ? <span className={invoice.archived ? "is2-status is2-status--archived" : statusClass(invoice.status)}>{t(invoice.archived ? "Archived" : invoice.status)}</span> : <InvoiceStatusControl invoice={invoice} onChange={updateInvoiceStatus} compact />}</td>
              <td className="is-number"><strong>{privacy.amount(formatCurrency(invoice.amount, invoice.currency))}</strong></td>
              <td><IconButton label={`Actions for ${privacy.invoice(invoice)}`} onClick={(event) => { event.stopPropagation(); const trigger = event.currentTarget; const rect = trigger.getBoundingClientRect(); const menuHeight = privacy.enabled ? 190 : invoice.archived ? 132 : 430; const placed = placeFloatingPanel({ anchor: rect, width: 245, height: menuHeight, align: "end", margin: 12, viewport: getVisualViewportBounds() }); setActionMenu({ invoice, trigger, position: { top: placed.top, left: placed.left, width: placed.width, maxHeight: placed.maxHeight } }); }}><MoreIcon size={15} /></IconButton></td>
            </tr>
          );})}
        </tbody>
      </table></div>
      {actionMenu ? <InvoiceActionMenu downloadAccountDocument={downloadAccountDocument} invoice={actionMenu.invoice} issuer={businesses.find((business) => business.id === actionMenu.invoice.issuerId)} clients={clients} position={actionMenu.position} menuRef={actionMenuRef} close={(options) => { const trigger = actionMenu.trigger; setActionMenu(null); if (options?.restoreFocus !== false) window.setTimeout(() => trigger?.focus(), 0); }} notify={notify} openInvoice={openInvoice} reuseInvoice={reuseInvoice} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} /> : null}
    </>
  );
};

export default InvoiceTable;
